/**
 * Cartão de emergência — cliente. O meu (da conta) se edita no Perfil; o do
 * grupo (por viagem) fica em cache no aparelho: é justamente sem sinal que
 * ele mais serve.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { localDb } from '../roteiro/persistence';

export interface Emergencia {
  phone: string | null;
  contactName: string | null;
  contactPhone: string | null;
  healthNotes: string | null;
  insuranceName: string | null;
  insurancePolicy: string | null;
  insurancePhone: string | null;
}
export interface CartaoDeEmergencia {
  memberId: string;
  userId: string;
  name: string;
  me: boolean;
  info: Emergencia | null;
}

export const VAZIO: Emergencia = { phone: null, contactName: null, contactPhone: null, healthNotes: null, insuranceName: null, insurancePolicy: null, insurancePhone: null };

async function pedir<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin', ...init, headers: { ...(init?.body ? { 'content-type': 'application/json' } : {}), ...(init?.headers ?? {}) } });
  if (!res.ok) {
    const corpo = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(corpo.message ?? `Falha (${res.status})`);
  }
  return (await res.json()) as T;
}

const chave = (tripId: string) => `emergencia:${tripId}`;

export function useMinhaEmergencia() {
  return useQuery({ queryKey: ['emergencia', 'minha'], queryFn: () => pedir<Emergencia>('/api/v1/me/emergency'), staleTime: 5 * 60_000 });
}

export function useGuardarEmergencia() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (dados: Emergencia) => pedir<Emergencia>('/api/v1/me/emergency', { method: 'PUT', body: JSON.stringify(dados) }),
    onSuccess: (dados) => {
      cliente.setQueryData(['emergencia', 'minha'], dados);
      void cliente.invalidateQueries({ queryKey: ['emergencia', 'viagem'] });
    },
  });
}

/**
 * O cartão do grupo, com cache: lê o aparelho primeiro, tenta o servidor, e
 * guarda o que veio. Sem rede e sem cache, `data` é `null` e a tela explica.
 */
export function useCartoesDaViagem(tripId: string) {
  return useQuery({
    queryKey: ['emergencia', 'viagem', tripId],
    queryFn: async (): Promise<{ cards: CartaoDeEmergencia[]; doCache: boolean } | null> => {
      const db = await localDb().catch(() => null);
      const guardado = await db?.cache.get<{ cards: CartaoDeEmergencia[] }>(chave(tripId)).catch(() => undefined);
      try {
        const fresco = await pedir<{ cards: CartaoDeEmergencia[] }>(`/api/v1/trips/${tripId}/emergency`);
        await db?.cache.set(chave(tripId), fresco).catch(() => undefined);
        return { ...fresco, doCache: false };
      } catch {
        return guardado ? { ...guardado, doCache: true } : null;
      }
    },
    staleTime: 60_000,
  });
}
